import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { MyPledgeResponse } from "../api/types";
import { Btn } from "../components/Btn";
import { UserAvatar } from "../components/UserAvatar";
import { useIsDesktop } from "../lib/useIsDesktop";
import { money, money2 } from "../lib/format";

export function MyPledge() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [resp, setResp] = useState<MyPledgeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    api.get<MyPledgeResponse>("/pledges/me").then(setResp).catch((e) => setError(e.message));
  }, []);

  if (error) return <div style={{ padding: 32 }}>Error: {error}</div>;
  if (!resp || !user) return <div style={{ padding: 32 }}>Loading…</div>;

  const pledge = resp.pledge;

  return (
    <div style={{ padding: isDesktop ? "64px 64px 80px" : "24px 22px 32px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <h1 style={{ margin: 0, fontFamily: "var(--display)", fontSize: isDesktop ? 44 : 32, fontWeight: 400, letterSpacing: -1 }}>
          My Pledge
        </h1>

        <div style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 10px 6px 6px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 999 }}>
          <UserAvatar first={user.first} last={user.last} size={26} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{user.first} {user.last}</span>
        </div>

        {pledge ? (
          <div style={{ marginTop: 24, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 18, padding: 24 }}>
            <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Your donation total so far
            </div>
            <div style={{ marginTop: 8, fontFamily: "var(--display)", fontSize: 56, fontWeight: 400, color: "var(--accent)", letterSpacing: -1.5, lineHeight: 1 }}>
              {money(pledge.owedNow)}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 6 }}>
              Scott has ridden {resp.miles} of {resp.goal} mi
            </div>

            <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--line)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <Label>Your rate</Label>
                <Value>{money2(pledge.rate)}/mi</Value>
              </div>
              <div>
                <Label>If he hits {resp.goal.toLocaleString()}</Label>
                <Value>{money(pledge.projected)}</Value>
              </div>
            </div>

            {resp.closed && resp.gofundme ? (
              <>
                <a href={resp.gofundme} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <Btn variant="primary" full style={{ marginTop: 22 }}>
                    Donate {money(pledge.owedNow)} on GoFundMe →
                  </Btn>
                </a>
                <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ink-soft)", textAlign: "center", lineHeight: 1.5 }}>
                  The fundraiser has closed — donate now via GoFundMe.
                </div>
              </>
            ) : (
              <>
                <Btn variant="secondary" full disabled style={{ marginTop: 22, background: "var(--line)", color: "var(--ink-soft)", cursor: "not-allowed" }}>
                  🔒 Donate {money(pledge.owedNow)} on GoFundMe
                </Btn>
                <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ink-soft)", textAlign: "center", lineHeight: 1.5 }}>
                  Donations open at the end of summer, once Scott finishes riding. We'll email you a GoFundMe link with your final amount.
                </div>
              </>
            )}
            <Btn variant="ghost" full onClick={() => nav("/pledge")} style={{ marginTop: 12 }}>
              Edit my pledge
            </Btn>
          </div>
        ) : (
          <div style={{ marginTop: 24, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 18, padding: 28, textAlign: "center" }}>
            <div style={{ fontFamily: "var(--display)", fontSize: 26, fontWeight: 400, letterSpacing: -0.5 }}>
              Join the crew
            </div>
            <p style={{ fontSize: 14, color: "var(--ink-soft)", marginTop: 8 }}>
              Set a rate per mile. No payment until Scott's done.
            </p>
            <Btn variant="primary" onClick={() => nav("/pledge")} style={{ marginTop: 14 }}>
              Make a Pledge
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function Value({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 4, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{children}</div>;
}
